import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators, FormValidationService } from "@app/directives/_forms/form-validation.service";
import { ICountry } from "@app/directives/_forms/smsInput/ICountry";
import { IPhoneNumberOutput } from "@app/directives/_forms/smsInput/smsInput.interfaces";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { AccountStatus, AvailableAccountStatus } from "@app/enum/account/AccountStatus";
import { Platform } from "@app/enum/account/Platform";
import { IGenericPreference } from "@app/interfaces/IGenericPreference";
import { IPreferencesContent } from "@app/interfaces/IPreferencesContent";
import { IAccount } from "@app/services/account.service";
import { PreferenceService } from "@app/services/accounts/preferences.service";
import { NavigationService } from "@app/services/navigation.service";
import { DistanceMeasurement, IPreferencesQueryOptions, JourneyCostType } from "@app/services/preferences.service";
import { ResellerAccountMappingService } from "@app/services/resellerAccountMapping.service";
import { ITranslationLocale, TranslationService } from "@app/services/translation.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import copy from "fast-copy";
import { combineLatest, forkJoin, Observable, of, Subscription, timer } from "rxjs";
import { map, mergeMap, switchMap, take } from "rxjs/operators";
import { IUser } from "../../../../../../interfaces/IUser";
import { CurrentUserService } from "../../../../../../services/currentUser.service";
import { FoundationNavigationModule } from "../../../foundation.navigation";
import { IReseller } from "../../../_models/IReseller";
import { AccountService } from "../../../_services/account.service";
import { UserService } from "../../../_services/user.service";
import './customer-edit.component.scss';

@UntilDestroy()
@Component({
    selector: 'foundation-customer-edit',
    template: require('./customer-edit.component.html')
})
export class FoundationCustomerEditComponent implements OnInit {
    @ViewChild('confirmSaveTemplate') confirmSaveTemplate: TemplateRef<any>;

    customerId: number;
    customer: IAccount;
    amendedCustomer: IAccount;

    customerImage$: Observable<string>;

    AccountStatus = AccountStatus;
    Platform = Platform;

    selectedResellerId?: number;
    mappedResellerIds?: number[] = [];
    resellers: IReseller[];

    isPhoneValid: boolean;
    currentUserCanUpdateAccountStatus$: Observable<boolean>;

    AvailableAccountStatus = AvailableAccountStatus;
    accountStatusList = Object.keys(this.AvailableAccountStatus).filter(s => !isNaN(Number(s))).map(s => Number(s));
    selectedStatus: AccountStatus;

    customerEditForm: FormGroup;
    accountPreferences: IPreferencesContent;
    amendedPreferences: IPreferencesContent;
    countryCodes$: Observable<ICountry[]>;
    timeZones$: Observable<ITimeZoneInfo[]>;
    locales: ITranslationLocale[];
    loadingLocales: boolean;

    loading: boolean;
    accountStatusChanged: boolean = false;

    journeyCostTypeLabel: string;

    formSubscription = new Subscription();

    dialog: VDialogRef;

    currentUser: IUser;

    constructor(
        private readonly accountService: AccountService,
        private readonly dialogService: DialogService,
        private readonly formValidationService: FormValidationService,
        private readonly navigationService: NavigationService,
        private readonly preferenceService: PreferenceService,
        private readonly translationService: TranslationService,
        private readonly userService: UserService,
        private readonly currentUserService: CurrentUserService,
        private readonly resellerAccountMappingService: ResellerAccountMappingService
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    onResellerChange($event: any): void {
        this.mappedResellerIds = $event;
        this.customerEditForm.get("MappedResellerIds").setValue($event);
    }

    onSmsOutputChanged(output: IPhoneNumberOutput): void {
        if (!this.amendedCustomer) {
            return;
        }

        if (!output.input) {
            this.isPhoneValid = true;
            this.amendedCustomer.Phone = null;
        }

        this.isPhoneValid = output.isValid;

        this.customerEditForm?.get("Phone").setValue(output.internationalized);
    }

    onPrefChanged(value: any, preference: IGenericPreference<any>, formName: string): void {
        this.customerEditForm.get(formName).setValue(value);
        preference.savePreference = true;
        this.updateJourneyCostTypeLabel(this.amendedPreferences);
    }
    
    confirmSave(): void {
        if (!this.formValidationService.checkFormValid(this.customerEditForm)) {
            return;
        }

        if (this.accountStatusChanged) {
            this.dialog = this.dialogService.open(this.confirmSaveTemplate, null);
            return;
        }
        this.saveCustomer();
    }

    confirmSaveAndCloseDialog(): void {
        this.closeDialog();
        this.saveCustomer();
    }

    cancelSave(): void {
        this.closeDialog();
    }

    closeDialog(): void {
        this.dialog.close();
    }

    closeEditor() {
        this.navigationService.go(FoundationNavigationModule.Customers);
    }

    setAccountStatusChanged(): void {
        this.accountStatusChanged = true;
    }

    saveCustomer(): void {
        this.loading = true;

        this.amendedPreferences.Timezone.savePreference = true// force to save 'default' timezone
        this.accountPreferences = copy(this.amendedPreferences);
        this.removeUnchangedProperties(this.accountPreferences);

        this.amendedCustomer.Status = this.amendedCustomer.Id > -1 ? this.amendedCustomer.Status : AccountStatus.Active;
        this.amendedCustomer.Phone = this.amendedCustomer.Phone ?? "";

        this.accountService.saveAccount(this.amendedCustomer).pipe(
            switchMap(account => timer(1000).pipe(map(() => account))), // Give time for the readonly replica 
            mergeMap(account => {
                this.customerId = account.Id;

                return forkJoin({
                    preferences: this.preferenceService.updateAccountPreferences(this.amendedPreferences, account.Id),
                    mappings: this.resellerAccountMappingService.updateMappings(this.mappedResellerIds, account.Id)
                });
            }),
            take(1)
        ).subscribe(() => {
            this.navigationService.go(FoundationNavigationModule.CustomerView, { customerId: this.customerId });
            this.loading = false;
        });
    }

    //--

    private loadData(): void {
        this.customerId = this.navigationService.getRouteParam('customerId');
        this.currentUserCanUpdateAccountStatus$ = this.userService.currentUserCanUpdateAccountStatus();
        let accountRequest = this.accountService.getCustomerAccount(this.customerId);

        if (isNaN(+this.customerId)) {
            accountRequest = this.createNewAccount()
        }

        this.currentUserService.getCurrentUser().then(currentUser => {
            this.currentUser = currentUser;
            
            combineLatest([accountRequest, this.accountService.getResellers(), this.resellerAccountMappingService.getMappings({ IsActive: true })])
                .pipe(untilDestroyed(this))
                .subscribe(([account, resellers, resellerMappings]) => {
                    this.resellers = resellers;
                    this.customer = account;
                    this.mappedResellerIds = resellerMappings.filter(m => m.AccountId === this.customer.Id).map(m => m.ResellerId);
                    this.setAmendedCustomer();
                    this.getPreferences();
                    this.getCountryCodes();
                    this.getTimezones();
                    this.getLocales();
                    this.getCustomerImage();
                });
        })
    }

    private setDefaultReseller(): void {
        const defaultReseller = this.resellers.find(x => x.IsDefault);

        if (!defaultReseller) {
            return;
        }

        this.selectedResellerId = defaultReseller.Id;
        this.customerEditForm.get("ResellerId").setValue(defaultReseller.Id);
    }

    private createForm(): void {
        if (!this.customer || !this.accountPreferences) {
            return;
        }

        this.customerEditForm = new FormGroup({
            // Customer Details
            CompanyName: new FormControl(this.customer.CompanyName, [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            FleetSize: new FormControl(this.customer.FleetSize),
            Website: new FormControl(this.customer.Website),
            Phone: new FormControl(this.customer.Phone),
            Status: new FormControl(this.customer.Status),
            MappedResellerIds: new FormControl(this.mappedResellerIds, [CustomFormValidators.minimumArrayLength(1)]),
            // General
            ShowInactiveDrivers: new FormControl(this.accountPreferences.ShowInactiveDrivers.Value),
            ShowVehiclesNoUnit: new FormControl(this.accountPreferences.ShowVehiclesNoUnit.Value),
            ShowVehiclesNotTracking: new FormControl(this.accountPreferences.ShowVehiclesNotTracking.Value),
            TrailerAssociations: new FormControl(this.accountPreferences.TrailerAssociations.Value),
            // Map
            Geofences: new FormControl(this.accountPreferences.Geofences.Value),
            ShowZones: new FormControl(this.accountPreferences.ShowZones.Value),
            ViewSystemGeofences: new FormControl(this.accountPreferences.ViewSystemGeofences.Value),
            GeofenceLabelWhenZoomedOut: new FormControl(this.accountPreferences.GeofenceLabelWhenZoomedOut.Value),
            Clustering: new FormControl(this.accountPreferences.Clustering.Value),
            VehicleLabel: new FormControl(this.accountPreferences.VehicleLabel.Value),
            VehicleLabelText: new FormControl(this.accountPreferences.VehicleLabelText.Value),
            // Cost
            JourneyCostType: new FormControl(this.accountPreferences.JourneyCostType.Value),
            JourneyCost: new FormControl(this.accountPreferences.JourneyCost.Value),
            Currency: new FormControl(this.accountPreferences.Currency.Value),
            // Measurement
            DistanceMeasurement: new FormControl(this.accountPreferences.DistanceMeasurement.Value),
            TemperatureMeasurement: new FormControl(this.accountPreferences.TemperatureMeasurement.Value),
            VolumeMeasurement: new FormControl(this.accountPreferences.VolumeMeasurement.Value),
            FuelConsumptionMeasurement: new FormControl(this.accountPreferences.FuelConsumptionMeasurement.Value),
            // Internationalisation
            CountryCode: new FormControl(this.accountPreferences.CountryCode.Value),
            Timezone: new FormControl(this.accountPreferences.Timezone.Value),
            Language: new FormControl(this.accountPreferences.Language.Value),
            //TMS
            RouteStartCondition: new FormControl(this.accountPreferences.RouteStartCondition.Value),
            RouteEndCondition: new FormControl(this.accountPreferences.RouteEndCondition.Value),
            VisitMatchCondition: new FormControl(this.accountPreferences.VisitMatchCondition.Value),
            ArriveOnTimeResidingInLocation: new FormControl(this.accountPreferences.ArriveOnTimeResidingInLocation.Value),
            OnSiteBufferTime: new FormControl(this.accountPreferences.OnSiteBufferTime.Value),
            TravelBufferTime: new FormControl(this.accountPreferences.TravelBufferTime.Value),
            MaximumTimeToRestartRoute: new FormControl(this.accountPreferences.MaximumTimeToRestartRoute.Value),
            MinimumStartTime: new FormControl(this.accountPreferences.MinimumStartTime.Value),
            MaximumStartTime: new FormControl(this.accountPreferences.MaximumStartTime.Value),
            MaximumExpiryTime: new FormControl(this.accountPreferences.MaximumExpiryTime.Value),
            MaximumLateTimeAllowed: new FormControl(this.accountPreferences.MaximumLateTimeAllowed.Value),
            CanMatchOnMissedVisits: new FormControl(this.accountPreferences.CanMatchOnMissedVisits.Value),
            RecordVisitsAsMissedIfVisitedOutOfOrder: new FormControl(this.accountPreferences.RecordVisitsAsMissedIfVisitedOutOfOrder.Value),
            RecordUnplannedVisits: new FormControl(this.accountPreferences.RecordUnplannedVisits.Value),
            RestartRouteOnReturnToStartingLocation: new FormControl(this.accountPreferences.RestartRouteOnReturnToStartingLocation.Value)
        })

        this.subscribeToFormChanges();
    }

    private createNewAccount(): Observable<IAccount> {
        return of({
            Id: 0,
            CompanyName: "",
            Phone: null,
            Image: "",
            Features: 0,
            Status: AccountStatus.Active
        })
    }

    private subscribeToFormChanges(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }

        this.customerEditForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === 'INVALID') {
                return;
            }

            this.amendedCustomer = {
                ...this.amendedCustomer,
                CompanyName: this.customerEditForm.value.CompanyName,
                FleetSize: this.customerEditForm.value.FleetSize,
                Website: this.customerEditForm.value.Website,
                Phone: this.customerEditForm.value.Phone,
                Status: this.customerEditForm.value.Status,
                ResellerId: this.customerEditForm.value.ResellerId
            }


            Object.keys(this.amendedPreferences).forEach(key => {
                this.amendedPreferences[key].Value = this.customerEditForm.value[key];
            })


        })
    }

    private setAmendedCustomer(): void {
        this.amendedCustomer = copy(this.customer);
        this.selectedStatus = this.amendedCustomer.Status;
    }

    private getPreferences(): void {
        let preferenceQueryOptions: IPreferencesQueryOptions = {};

        if (this.customer) {
            preferenceQueryOptions = {
                isAccountPreferences: true,
                AccountId: this.customer.Id
            };
        }

        this.preferenceService.getAccountPreferences(preferenceQueryOptions).pipe(untilDestroyed(this)).subscribe(preferences => {
                this.updateJourneyCostTypeLabel(preferences);
                this.accountPreferences = preferences;
                this.amendedPreferences = copy(preferences);
                this.createForm();
                this.setDefaultReseller()
            })
    }

    private getCountryCodes(): void {
        this.countryCodes$ = this.preferenceService.getCountryCodes();
    }

    private getTimezones(): void {
        this.timeZones$ = this.preferenceService.getTimezones();
    }

    private getLocales(): void {
        this.loadingLocales = true;
        this.translationService.getListOfLocales().then(locales => {
            this.locales = locales.filter(locale => locale.Locale != "qa" && locale.Locale != "no");
            this.loadingLocales = false;
        })
    }

    private updateJourneyCostTypeLabel(accountPreferences: IPreferencesContent): void {
        let translationPhrase: string = "UserPreferences.costPerMile";

        if (accountPreferences.JourneyCostType.Value == JourneyCostType.Distance) {
            if (accountPreferences.DistanceMeasurement.Value == DistanceMeasurement.Miles) {
                translationPhrase = "UserPreferences.costPerMile";

            } else if (accountPreferences.DistanceMeasurement.Value == DistanceMeasurement.Kilometres) {
                translationPhrase = "UserPreferences.costPerKilometre";

            }
        } else if (accountPreferences.JourneyCostType.Value == JourneyCostType.Hourly) {
            translationPhrase = "UserPreferences.costPerHour";
        }

        this.journeyCostTypeLabel = this.translationService.t(translationPhrase);
    }

    private removeUnchangedProperties(model: IPreferencesContent): void {
        for (const property in model) {
            const preference = model[property];

            if (preference && model[property].savePreference) {
                continue;
            }

            delete model[property];
        }
    }

    private getCustomerImage(): void {
        this.customerImage$ = this.accountService.getCustomerAccountImage(this.customerId);
    }
}
